<script setup>
  import { ref, watch } from "vue";
  import draggable from "vuedraggable";
  import { useChartTemplatesStore, getBaseTypeLabel } from "@stores/chartTemplates/chartTemplates";
  import { ScrollArea } from "@/components/ui/scroll-area";
  import { Button } from "@/components/ui/button";
  import { Badge } from "@/components/ui/badge";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ConfirmDialog } from "@/components/episto-ui/confirm-dialog";
  import { useConfirmDelete } from "@/utils/composables/useConfirmDelete";
  import { Plus, Copy, Trash2, GripVertical } from "lucide-vue-next";

  const store = useChartTemplatesStore();

  const props = defineProps({
    templates: {
      type: Array,
      required: true,
    },
    selectedId: {
      type: [Number, String],
      default: null,
    },
  });

  const emit = defineEmits(["select", "add", "duplicate", "delete", "reorder"]);

  const localTemplates = ref([...props.templates]);

  watch(
    () => props.templates,
    (newTemplates) => { localTemplates.value = [...newTemplates]; },
    { deep: true },
  );

  const {
    isOpen: showDeleteDialog,
    target: templateToDelete,
    requestDelete: openDeleteDialog,
    confirm: confirmDelete,
  } = useConfirmDelete((template) => emit("delete", template.id));

  function getDisplayName(template) {
    const effectiveData = store.getEffectiveTemplateData(template.id) || template;
    return effectiveData.name || "Untitled";
  }

  function getTypeLabel(template) {
    const effectiveData = store.getEffectiveTemplateData(template.id) || template;
    return effectiveData.base_type ? getBaseTypeLabel(effectiveData.base_type) : "No type";
  }

  function handleDragEnd() {
    emit("reorder", localTemplates.value.map((t) => t.id));
  }
</script>

<template>
  <div class="h-full flex flex-col">
    <ScrollArea class="flex-1 min-h-0">
      <div class="px-2 py-2">
        <draggable
          v-model="localTemplates"
          item-key="id"
          handle=".card-drag-handle"
          ghost-class="opacity-50"
          @end="handleDragEnd">
          <template #item="{ element: template }">
            <div
              :class="[
                'flex items-center gap-1.5 px-2 py-1.5 rounded-md cursor-pointer border transition-all group',
                selectedId === template.id
                  ? 'border-border bg-accent ring-2 ring-primary'
                  : 'border-transparent hover:border-border hover:bg-accent/50',
              ]"
              @click="$emit('select', template.id)">

              <!-- Drag handle -->
              <div
                class="card-drag-handle opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing flex-shrink-0">
                <GripVertical class="h-4 w-4 text-muted-foreground" />
              </div>

              <!-- Name + type -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium truncate">{{ getDisplayName(template) }}</p>
                <p class="text-xs text-muted-foreground truncate">{{ getTypeLabel(template) }}</p>
              </div>

              <!-- Hover actions -->
              <div
                class="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                @click.stop>
                <Tooltip content="Add template">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="h-6 w-6 text-muted-foreground hover:text-foreground"
                    @click="$emit('add')">
                    <Plus class="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip content="Duplicate">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="h-6 w-6 text-muted-foreground hover:text-foreground"
                    @click="$emit('duplicate', template.id)">
                    <Copy class="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
                <Tooltip content="Delete">
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    class="h-6 w-6 text-muted-foreground hover:text-destructive"
                    @click="openDeleteDialog(template)">
                    <Trash2 class="h-3.5 w-3.5" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </template>
        </draggable>
      </div>
    </ScrollArea>

    <!-- Footer -->
    <div
      class="py-2 px-3 border-t bg-background shrink-0 flex items-center justify-between">
      <Button
        variant="outline"
        class="gap-2"
        @click="$emit('add')">
        <Plus class="w-4 h-4" />
        Add Template
      </Button>
      <Badge
        variant="secondary"
        class="rounded-full">
        {{ templates.length }} template{{ templates.length !== 1 ? "s" : "" }}
      </Badge>
    </div>

    <ConfirmDialog
      v-model:open="showDeleteDialog"
      title="Delete Chart Template"
      confirm-label="Delete"
      @confirm="confirmDelete">
      Are you sure you want to delete "{{ templateToDelete?.name }}"? This action cannot be undone.
    </ConfirmDialog>
  </div>
</template>
